import { ROLE_PROFILE_TYPES } from "views_shared/app/javascript/constants/profileTypes";
import {
  createResource,
  updateResource,
  deleteResource,
  getResource,
  parseResponseBody,
  postgRESTApiUrl,
  resolveApiUrl,
} from "./api";
import { toAttachmentParam } from "./messages";
import { fetchWithTokenRefresh } from "./withTokenRefresh";

/**
 * Create a new conversation together with its first message — a conversation cannot exist without
 * at least one message (see Interact::Conversation).
 *
 * @param {object} args
 * @param {string} args.subject - Thread subject (required by the backend, 1..200 chars)
 * @param {string} args.body - First message body (HTML when contentFormat is "html")
 * @param {string} args.mailbox - "focused" | "other"
 * @param {Array<string|number>} args.receiverIds - Receiver profile slugs or ids (current user added server-side)
 * @param {string} [args.originType] - e.g. "Id::PromoEntity::Expert" (omit for none)
 * @param {string|number} [args.originId]
 * @param {string} [args.contentFormat] - "html" | "plain" (default "html")
 * @param {Array<object>} [args.attachments] - client attachment items (mapped to backend shape)
 * @param {boolean} [args.videoRequest]
 * @returns {Promise<{data?: object, error?: object}>} data.data.attributes.conversation_id on success
 */
const create = async ({
  subject,
  body,
  mailbox,
  receiverIds,
  originType = null,
  originId = null,
  contentFormat = "html",
  attachments = [],
  videoRequest = false,
}) => {
  const attributes = {
    subject,
    body,
    mailbox,
    receiver_ids: receiverIds,
    content_format: contentFormat,
    attachments: attachments.map(toAttachmentParam),
    video_request: !!videoRequest,
  };

  if (originId && originType) {
    attributes.origin_id = originId;
    attributes.origin_type = originType;
  }

  return createResource(`/interact/api/messaging/conversations`, { data: { attributes } });
};

/**
 * Fetch conversations for a receiver
 * @param {number} receiverProfileId - The receiver's numeric profile id. Stable across
 *   profile renames; the slug is NOT (a rename leaves historical rows on the old slug).
 * @param {object} options - Filter and pagination options
 * @param {string} options.mailbox - Filter by mailbox ("focused" | "other")
 * @param {string} options.readStatus - Filter by read status ("unread" | "read" | null)
 * @param {string} options.role - Filter by sender role ("experts" | "prs" | null)
 * @param {number} options.requestId - Posts filter: the request whose pitch conversations to show
 * @param {boolean} options.showTrashed - When true, show only trashed (archived) conversations
 * @param {object} options.cursor - Keyset cursor: `{ lastSentAt, conversationId }` for the next page; null = first page
 * @param {number} options.limit - Page size (default 20)
 * @returns {Promise<{data: Array, error?: object, remainingRows?: number, totalRows?: number}>}
 */
const list = async (
  receiverProfileId,
  {
    mailbox = null,
    readStatus = null,
    role = null,
    requestId = null,
    showTrashed = false,
    cursor = null,
    limit = 20,
    conversationId = null,
  } = {},
) => {
  let readFilter = [];
  if (readStatus === "unread") readFilter = [["receipt_read_at", "is.null"]];
  else if (readStatus === "read") readFilter = [["receipt_read_at", "not.is.null"]];

  const roleTypes = ROLE_PROFILE_TYPES[role];
  // Filter on co_participant_profile_types (sender types minus the receiver themselves)
  // so "<role> only" surfaces threads where the OTHER party has that role — instead of
  // matching every conversation the receiver has replied to.
  const roleFilter = roleTypes ? [["co_participant_profile_types", `ov.{${roleTypes.join(",")}}`]] : [];

  const archiveFilter = showTrashed ? [["receipt_archived_at", "not.is.null"]] : [["receipt_archived_at", "is.null"]];

  // Posts filter keys on request identity, never subject text (97% of pitch
  // conversations store "Re: " + a truncated title, which no string match can reach).
  const requestFilter = requestId ? [["origin_request_id", `eq.${requestId}`]] : [];

  // Compound keyset cursor — tiebreaks last_sent_at ties on conversation_id so paging is stable.
  const cursorFilter = cursor
    ? [
        [
          "or",
          `(last_sent_at.lt.${cursor.lastSentAt},and(last_sent_at.eq.${cursor.lastSentAt},conversation_id.lt.${cursor.conversationId}))`,
        ],
      ]
    : [];

  // Single-conversation lookup (realtime sync) — applies the same filters so the caller
  // can tell whether the conv still belongs in the current view.
  const idFilter = conversationId ? [["conversation_id", `eq.${conversationId}`]] : [];

  const params = new URLSearchParams([
    // Filter by the stable integer profile id, NOT the slug: slugs are friendly IDs that
    // change when a user renames their profile, which would hide their pre-rename history.
    ["receiver_profile_id", `eq.${receiverProfileId}`],
    ["order", "last_sent_at.desc,conversation_id.desc"],
    ["limit", String(limit)],
    ...(mailbox ? [["conversation_mailbox", `eq.${mailbox}`]] : []),
    ...readFilter,
    ...roleFilter,
    ...requestFilter,
    ...archiveFilter,
    ...cursorFilter,
    ...idFilter,
  ]);

  const url = `${postgRESTApiUrl()}/rest/v1/conversations?${params}`;
  return getResource(url);
};

// Batch lookup of the conversations born from these pitches (receiver-scoped; a pitch
// without a conversation returns no row). Includes archived so trashed threads still
// count as "messaged". pitchIds are V2 pitch ids (= conversation origin_id).
const getByPitchOriginIds = async (receiverProfileId, pitchIds) => {
  if (!pitchIds.length) return { data: [] };
  const params = new URLSearchParams([
    ["receiver_profile_id", `eq.${receiverProfileId}`],
    ["origin_type", "eq.Interact::Pitch"],
    ["origin_id", `in.(${pitchIds.join(",")})`],
  ]);
  const url = `${postgRESTApiUrl()}/rest/v1/conversations?${params}`;
  return getResource(url);
};

/**
 * Look up a single conversation by V1 numeric id (migrate_id) or V2 uuid,
 * without a mailbox filter. Used by the deep-link resolver to find which
 * mailbox a conversation lives in so the inbox can switch tabs to show it.
 */
const findById = async ({ receiverProfileId, v1Id = null, v2Uuid = null }) => {
  if (!v1Id && !v2Uuid) return { data: [] };
  const idFilter = v1Id ? ["migrate_id", `eq.${v1Id}`] : ["conversation_id", `eq.${v2Uuid}`];
  const params = new URLSearchParams([["receiver_profile_id", `eq.${receiverProfileId}`], idFilter, ["limit", "1"]]);
  const url = `${postgRESTApiUrl()}/rest/v1/conversations?${params}`;
  return getResource(url);
};

const markReadById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/read`;
  return updateResource(url, {});
};

/**
 * Mark multiple conversations as read
 * @param {string[]} ids - Array of conversation UUIDs
 * @returns {Promise<{data: Array, errors: Array}>}
 */
const markReadByIds = async (ids) => {
  const results = await Promise.all(ids.map((id) => markReadById(id)));
  const errors = results.filter((r) => r.error);
  return { data: results.filter((r) => r.data), errors };
};

const markAllRead = async () => {
  const url = "/interact/api/messaging/conversations/mark_all_read";
  return createResource(url, {});
};

/**
 * Mark a conversation as unread
 * @param {string} id - The conversation UUID
 */
const markUnreadById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/unread`;
  return createResource(url, {});
};

/**
 * Mute a conversation
 * @param {string} id - The conversation UUID
 */
const muteById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/mute`;
  return createResource(url, {});
};

/**
 * Unmute a conversation
 * @param {string} id - The conversation UUID
 */
const unmuteById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/mute`;
  return deleteResource(url);
};

/**
 * Flag/report a conversation
 * @param {string} id - The conversation UUID
 */
const flagById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/flag`;
  return createResource(url, {});
};

/**
 * Archive a conversation
 * @param {string} id - The conversation UUID
 */
const archiveById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/archive`;
  return deleteResource(url);
};

/**
 * Archive multiple conversations
 * @param {string[]} ids - Array of conversation UUIDs
 * @returns {Promise<{data: Array, errors: Array}>}
 */
const archiveByIds = async (ids) => {
  const results = await Promise.all(ids.map((id) => archiveById(id)));
  const errors = results.filter((r) => r.error);
  return { data: results.filter((r) => r.data), errors };
};

/**
 * Restore a conversation from trash (un-archive)
 * @param {string} id - The conversation UUID
 */
const restoreById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/restore`;
  return createResource(url, {});
};

/**
 * Permanently delete a conversation (destroys receipts — cannot be undone)
 * @param {string} id - The conversation UUID
 */
const permanentDeleteById = async (id) => {
  const url = `/interact/api/messaging/conversations/${id}/permanent_destroy`;
  return deleteResource(url);
};

/**
 * Search conversations by query text (searches across all mailbox tabs)
 * @param {string} query - The search text
 * @param {object} options - Options
 * @param {AbortSignal} options.signal - AbortController signal for cancellation
 * @returns {Promise<{data: Array, error?: object}>}
 */
const search = async (query, { signal = null, filters = null } = {}) => {
  // The inbox's Posts / Status / Role filters narrow the hits, as they narrow the list.
  const filterParams = [
    ["filter[posts]", filters?.posts],
    ["filter[status]", filters?.status],
    ["filter[role]", filters?.role],
  ].filter(([, value]) => value);
  const params = new URLSearchParams([
    ["filter[query]", query],
    // Opt-in to pitch-typed results — clients without pitch-aware normalizers (the
    // deployed V1 mirror until its port lands) omit this and get conversations only.
    ["filter[include_pitches]", "true"],
    ...filterParams,
    ["limit", "9999"],
  ]);

  const url = `/interact/api/messaging/conversation_searches?${params}`;
  // resolveApiUrl: no-op here, but the V1 mirror copy must cross origin like every
  // other Rails-API call in this file (search bypassed the helpers that do it).
  const response = await fetchWithTokenRefresh(resolveApiUrl(url), {
    method: "GET",
    ...(signal && { signal }),
  });

  const body = await parseResponseBody(response);

  if (response.ok) {
    return { data: body?.data };
  }

  return {
    error: {
      status: response.status,
      statusText: response.statusText,
      body,
    },
  };
};

const addReceiver = async (conversationId, receiverId) => {
  const url = `/interact/api/messaging/conversations/${conversationId}/receivers`;
  return createResource(url, { data: { attributes: { new_receiver_id: receiverId } } });
};

/**
 * Fetch the current viewer's conversations that are ABOUT a promo entity (Expert/Product) —
 * origin + last-activity columns only — for a "last message sent" lookup keyed by origin.
 * Mirrors favorite_groups.js's fetchMembershipIndex: one lean fetch, dedup left to the caller.
 *
 * Scoped by receiver_profile_id on purpose: the `conversations` view's RLS lets a user see BOTH
 * their own and their teammates' receiver rows, so an unscoped fetch would surface a teammate's
 * thread as the current user's. Filtering to the caller's own profile id matches my-inbox.
 *
 * @param {number|string} receiverProfileId - the viewer's own profile id (globalThis.Qwoted.CURRENT_PROFILE_ID)
 * @returns {Promise<{data: Array, error?: object}>} rows: { origin_id, origin_type, last_sent_at }, last_sent_at DESC
 */
const fetchLastMessageIndex = async (receiverProfileId) => {
  const params = new URLSearchParams([
    ["receiver_profile_id", `eq.${receiverProfileId}`],
    ["origin_type", 'in.("Id::PromoEntity::Expert","Id::PromoEntity::Product")'],
    ["select", "origin_id,origin_type,last_sent_at"],
    ["order", "last_sent_at.desc"],
    ["limit", "10000"],
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/conversations?${params}`);
};

/**
 * Find the current viewer's most-recent conversation ABOUT a given promo entity, if any.
 * Used to open an existing inbox thread instead of composing a new one. Scoped to the viewer's
 * own receiver rows (RLS also exposes teammates' — we want the caller's own thread).
 *
 * @param {object} args
 * @param {number|string} args.receiverProfileId - the viewer's own profile id (globalThis.Qwoted.CURRENT_PROFILE_ID)
 * @param {string} args.originType - e.g. "Id::PromoEntity::Expert"
 * @param {number|string} args.originId
 * @returns {Promise<{data: Array, error?: object}>} data: 0 or 1 rows of { conversation_id }
 */
/**
 * Fetch the current viewer's peer (profile-to-profile) direct-message conversations — the
 * co-participant profile ids + last-activity column only — for a "last message sent" lookup keyed
 * by the OTHER participant. Parallels fetchLastMessageIndex, but source-directory PR cards are
 * messaged profile-to-profile and so carry no promo-entity origin to key on; they key on the
 * co-participant instead. Scoped to direct messages — threads with no promo-entity/pitch origin, i.e.
 * a null origin (legacy profile-to-profile DMs) or an `Id::Profile` origin (the contacted profile) —
 * so pitch and promo-entity threads don't leak in.
 *
 * Scoped by receiver_profile_id for the same RLS reason as fetchLastMessageIndex: the view exposes
 * teammates' receiver rows too, and we want only the caller's own threads.
 *
 * @param {number|string} receiverProfileId - the viewer's own profile id (globalThis.Qwoted.CURRENT_PROFILE_ID)
 * @returns {Promise<{data: Array, error?: object}>} rows: { co_participant_profile_ids, last_sent_at }, last_sent_at DESC
 */
const fetchPeerLastMessageIndex = async (receiverProfileId) => {
  const params = new URLSearchParams([
    ["receiver_profile_id", `eq.${receiverProfileId}`],
    ["or", "(origin_type.is.null,origin_type.like.Id::Profile*)"],
    ["select", "co_participant_profile_ids,last_sent_at"],
    ["order", "last_sent_at.desc"],
    ["limit", "10000"],
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/conversations?${params}`);
};

const findLatestByOrigin = async ({ receiverProfileId, originType, originId }) => {
  const params = new URLSearchParams([
    ["receiver_profile_id", `eq.${receiverProfileId}`],
    ["origin_type", `eq.${originType}`],
    ["origin_id", `eq.${originId}`],
    ["select", "conversation_id"],
    ["order", "last_sent_at.desc"],
    ["limit", "1"],
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/conversations?${params}`);
};

/**
 * Find the viewer's existing one-to-one direct-message thread with a single other profile (a PR /
 * promoter), if any — the peer-DM analogue of findLatestByOrigin. Source-directory PR cards carry
 * no promo-entity origin to key on, so "Start a Conversation" must dedup on the co-participant
 * instead: reuse the pair's existing thread rather than composing a duplicate.
 *
 * `co_participant_profile_ids=eq.{id}` matches exactly-one-other-party rows (the array excludes the
 * viewer themselves), so a group thread that merely includes the profile isn't hijacked. Scoped to
 * direct messages (null or `Id::Profile` origin) so a promo-entity/pitch thread can't match even
 * when the other party is an expert's self-rep. Scoped to the viewer's own receiver rows for the
 * same RLS reason as fetchLastMessageIndex.
 *
 * @param {object} args
 * @param {number|string} args.receiverProfileId - the viewer's own profile id (globalThis.Qwoted.CURRENT_PROFILE_ID)
 * @param {number|string} args.coParticipantProfileId - the other party's profile id (the PR card's hit.id)
 * @returns {Promise<{data: Array, error?: object}>} data: 0 or 1 rows of { conversation_id }
 */
const findLatestPeerConversation = async ({ receiverProfileId, coParticipantProfileId }) => {
  const params = new URLSearchParams([
    ["receiver_profile_id", `eq.${receiverProfileId}`],
    ["co_participant_profile_ids", `eq.{${coParticipantProfileId}}`],
    ["or", "(origin_type.is.null,origin_type.like.Id::Profile*)"],
    ["select", "conversation_id"],
    ["order", "last_sent_at.desc"],
    ["limit", "1"],
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/conversations?${params}`);
};

export default {
  addReceiver,
  archiveById,
  archiveByIds,
  create,
  fetchLastMessageIndex,
  fetchPeerLastMessageIndex,
  findLatestByOrigin,
  findLatestPeerConversation,
  findById,
  getByPitchOriginIds,
  flagById,
  list,
  markAllRead,
  markReadById,
  markReadByIds,
  markUnreadById,
  muteById,
  permanentDeleteById,
  restoreById,
  search,
  unmuteById,
};
